<template>
  <div class="w-full h-full">
    <div
      class="gap-4 w-full h-full relative rounded-3xl overflow-hidden border"
      v-if="item?.id || item?.shimmer"
      :class="{
        shimmerElementRoundedDefault: item.shimmer
      }"
      @click="showDetails(item)"
    >
      <div class="items-center grid grid-cols-3">
        <div class="relative col-span-1 h-full py-[60%]">
          <div class="absolute w-full h-full top-0 left-0 overflow-hidden">
            <ImgComponent
              class="relative w-full h-full object-cover"
              :src="item.principalImageUrl"
              alt=""
            />
            <div
              class="absolute w-[200%] h-full top-0 left-0 bg-linear-to-l from-[#fff8] to-transparent"
            ></div>
            <!-- Promotion badge -->
            <div
              v-if="item.promotion"
              class="absolute left-2 top-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-primary text-white shadow-md z-[2]"
            >
              <template v-if="item.promotion.discountType === 'percentage'">
                -{{ item.promotion.discount }}%
              </template>
              <template v-else-if="item.promotion.discountType === 'fixed'">
                -{{ item.promotion.discount }} F
              </template>
            </div>
          </div>
        </div>
        <div class="col-span-2 h-full relative">
          <div
            class="w-full flex justify-between flex-col p-4 pl-2 absolute liquidGlassCard h-full rounded-r-xl border-y-0 border-r-0"
          >
            <div class="flex items-center">
              <div class="font-bold title-color w-full truncate">
                {{ item.name }}
              </div>
              <div class="flex gap-1 text-xs">
                <div class="w-4 h-4">
                  <ImgComponent
                    src="/images/all/history-primary.svg"
                    alt=""
                    class="w-full h-full"
                  />
                </div>
                <div>{{ item.cookDurationInMin }}min</div>
              </div>
            </div>
            <div class="text-xs">
              <i
                class="text-xs text-primary"
                v-for="el in 5"
                :key="el"
                :class="{
                  'fas fa-star': el <= 4,
                  'far fa-star': el > 4
                }"
              ></i>
            </div>
            <div class="flex flex-col gap-2 pt-0">
              <div class="flex items-center gap-2">
                <div class="text-primary font-bold truncate">
                  {{
                    item.restaurant?.name?.name ||
                    item.restaurant?.name ||
                    'Restaurant'
                  }}
                </div>
                <div
                  class="bg-[#F4F4F5] px-3 py-1 rounded-xl text-xs inline-block whitespace-nowrap mr-2"
                >
                  {{ item.category }}
                </div>
              </div>
              <div class="flex justify-between items-center">
                <div class="flex items-end gap-1">
                  <div
                    class="whitespace-nowrap flex gap-1 title-color font-bold"
                    :class="item.promotion ? 'text-lg underline' : 'underline'"
                  >
                    {{
                      item.promotion
                        ? item.promotion.promotionalPrice
                        : item.price
                    }}
                    CFA
                  </div>
                  <div
                    v-if="item.promotion"
                    class="text-xs text-slate-400 line-through font-medium mb-[2px]"
                  >
                    {{ item.price }} CFA
                  </div>
                </div>

                <div
                  @click.stop="addToCart"
                  class="ripple relative z-[4] text-2xl rounded-full min-w-9 w-9 h-9 p-1.5 flex items-center justify-center transition-all duration-300"
                  :class="{
                    'text-white bg-primary shadow-lg shadow-primary/30':
                      isAddedToCart,
                    'text-primary bg-primary': !isAddedToCart
                  }"
                >
                  <ColorImg
                    v-if="!isAddedToCart"
                    src="/images/all/add-to-cart.svg"
                    class="bg-white w-full h-full"
                    alt=""
                  />
                  <ImgComponent
                    v-else
                    src="/images/all/check-white.svg"
                    class="w-full h-full p-0.5"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div></div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import constants from '@core/utils/constants';

export default defineComponent({
  name: 'NearDishCard',
  props: {
    item: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      showPlaceHolder: false
    };
  },
  mounted() {
    setTimeout(() => {
      this.showPlaceHolder = true;
    }, 5000);
  },
  beforeUnmount() {},
  computed: {
    isAddedToCart() {
      const cartItems = window.$stores?.cart?.state?.cart?.items || [];
      return cartItems.some((item) => item.dishId === this.item.id);
    },
    cartItemId() {
      const cartItems = window.$stores?.cart?.state?.cart?.items || [];
      const found = cartItems.find((item) => item.dishId === this.item.id);
      return found ? found.id : null;
    }
  },
  methods: {
    showDetails(dish) {
      if (!dish || !dish?.id) return;
      this.$bus.emit('dish.SHOW_DISH_DETAIL', {
        id: dish.id
      });
    },
    async addToCart() {
      if (!this.item || !this.item.id) {
        console.error('Invalid item for adding to cart');
        return;
      }

      // Toggle logic: If already in cart, remove it
      if (this.isAddedToCart && this.cartItemId) {
        this.$bus.emit(constants.EVENTS.CART.ITEMS.REMOVE, {
          itemId: this.cartItemId
        });
        return;
      }

      // Emit cart add event to trigger API call and store update
      console.log('🚀 ~ this.item:', this.item);
      this.$bus.emit(constants.EVENTS.CART.ITEMS.ADD, {
        restaurantId:
          this.item.storeId ||
          this.item.restaurant?.name?.id ||
          this.item.restaurant?.id,
        restaurant: this.item.restaurant,
        restaurantName:
          this.item.restaurant?.name?.name ||
          this.item.restaurant?.name ||
          this.item.storeName ||
          'Restaurant',
        dishId: this.item.id,
        title: this.item.name,
        name: this.item.name,
        price: this.item.promotion
          ? this.item.promotion.promotionalPrice
          : this.item.price,
        originalPrice: this.item.promotion ? this.item.price : undefined,
        promotion: this.item.promotion || undefined,
        quantity: 1,
        currency: this.item.currency || 'XOF',
        description: this.item.description,
        category: this.item.category,
        categoryId: this.item.categoryId,
        principalImageUrl: this.item.principalImageUrl || this.item.image,
        options: [],
        drinks: [],
        fullOptions: this.item.options,
        fullAccompaniments: this.item.accompaniments
      });
    }
  }
});
</script>
